import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Page } from "../../components/Page";
import { useCustomer360 } from "../../helpers/hooks/use-customer-360";
import { useRefreshMarketing } from "../../helpers/hooks/use-refresh-marketing";
import { OverviewSection } from "./sections/OverviewSection";
import { ContractBillingSection } from "./sections/ContractBillingSection";
import { TimelineSection } from "./sections/TimelineSection";
import { AppUsageSummaryCard } from "./sections/AppUsageSummaryCard";
import { MeetingsSection } from "./sections/MeetingsSection";
import { EmailTimelineSection } from "./sections/EmailTimelineSection";
import { AiSummarySection } from "./sections/AiSummarySection";
import { MarketingSection } from "./sections/MarketingSection";
import { HealthSection } from "./sections/HealthSection";
import { FeedbackPreview } from "./sections/FeedbackPreview";
import { CampaignSummaryTable } from "./sections/CampaignSummaryTable";
import { OwnersSection } from "./sections/OwnersSection";
import { NotesSection } from "./sections/NotesSection";
import { NextActionSection } from "./sections/NextActionSection";
import { ChurnRiskFlagSection } from "./sections/ChurnRiskFlagSection";
import { LinkedAccountsSection } from "../users/users-details/linked-accounts/LinkedAccountsSection";
import { EmailMarketingSection } from "./sections/EmailMarketingSection";
import { WebsiteBuilderSection } from "./sections/WebsiteBuilderSection";
import { AdsOptimizationSection } from "./sections/AdsOptimizationSection";
import { AdsOptimizationDecisionLog } from "./sections/AdsOptimizationDecisionLog";
import { AdsOptimizationConfigHistory } from "./sections/AdsOptimizationConfigHistory";
import {
  ComposeEmailModal,
  type ComposeContext,
} from "./ComposeEmailModal";
import {
  buildAutoLoginUrl,
  requestImpersonationToken,
} from "../../helpers/api/crm";

type TabId =
  | "overview"
  | "billing"
  | "communications"
  | "meetings"
  | "app-activity"
  | "ads-optimization";

// ---------------------------------------------------------------------------
// Shared formatters
// ---------------------------------------------------------------------------

function formatUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function relativeShort(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "Today";
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

// ---------------------------------------------------------------------------
// Hero strip tile — sits in a symmetrical grid, fills its cell fully
// ---------------------------------------------------------------------------

function Tile({
  label,
  value,
  accent,
  sub,
}: {
  label: string;
  value: string | number;
  accent?: "neutral" | "green" | "amber" | "red";
  sub?: string;
}) {
  const accentCls =
    accent === "green"
      ? "text-emerald-700"
      : accent === "amber"
        ? "text-amber-700"
        : accent === "red"
          ? "text-rose-700"
          : "text-content-primary";
  return (
    <div className="flex flex-col justify-center px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-content-tertiary font-medium">
        {label}
      </div>
      <div
        className={clsx(
          "text-lg font-semibold tabular-nums leading-tight mt-0.5",
          accentCls,
        )}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-content-tertiary mt-0.5 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab pill bar — minimal 3-tab switcher
// ---------------------------------------------------------------------------

function TabBar({
  active,
  onChange,
  openTickets,
  onOpenApp,
  openingApp,
}: {
  active: TabId;
  onChange: (t: TabId) => void;
  openTickets: number | null | undefined;
  onOpenApp?: () => void;
  openingApp?: boolean;
}) {
  const tabs: Array<{ id: TabId; label: string; hint?: string }> = [
    { id: "overview", label: "Overview" },
    { id: "billing", label: "Contract & Billing" },
    {
      id: "communications",
      label: "Communications",
      hint: openTickets ? String(openTickets) : undefined,
    },
    { id: "meetings", label: "Meetings" },
    { id: "app-activity", label: "App Activity" },
    { id: "ads-optimization", label: "Ads Optimization" },
  ];
  return (
    <nav className="inline-flex self-start items-center gap-1 bg-gray-100 rounded-full p-1 w-fit">
      {tabs.map((t) => {
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={clsx(
              "px-3.5 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap",
              isActive
                ? "bg-white text-content-primary shadow-sm"
                : "text-content-secondary hover:text-content-primary",
            )}
          >
            {t.label}
            {t.hint && (
              <span
                className={clsx(
                  "ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-semibold",
                  isActive
                    ? "bg-rose-100 text-rose-700"
                    : "bg-rose-50 text-rose-600",
                )}
              >
                {t.hint}
              </span>
            )}
          </button>
        );
      })}
      {onOpenApp && (
        <>
          {/* Subtle separator so it reads as an action, not a navigation tab. */}
          <span aria-hidden className="w-px h-4 bg-gray-300 mx-1" />
          <button
            type="button"
            onClick={onOpenApp}
            disabled={openingApp}
            title="Open the customer app logged in as this customer (1-hour session, audit-logged)"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium text-content-secondary hover:text-content-primary hover:bg-white/70 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span>{openingApp ? "Opening…" : "Open App"}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-3 h-3"
              aria-hidden
            >
              <path d="M14 3h7v7" />
              <path d="M21 3l-9 9" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          </button>
        </>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function PageSkeleton() {
  return (
    <div className="grid grid-cols-12 gap-3 animate-pulse">
      <div className="col-span-12 h-14 bg-gray-100 rounded-xl" />
      <div className="col-span-12 lg:col-span-7 h-72 bg-gray-100 rounded-xl" />
      <div className="col-span-12 lg:col-span-5 h-72 bg-gray-100 rounded-xl" />
      <div className="col-span-12 h-56 bg-gray-100 rounded-xl" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useCustomer360(id ?? "");
  const refreshMarketing = useRefreshMarketing(id ?? "");
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [openingApp, setOpeningApp] = useState(false);

  const handleOpenApp = async () => {
    if (!id || openingApp) return;
    setOpeningApp(true);
    try {
      const { accessToken } = await requestImpersonationToken(id);
      window.open(
        buildAutoLoginUrl(accessToken),
        "_blank",
        "noopener,noreferrer",
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Request failed.";
      window.alert(`Could not open app: ${msg}`);
    } finally {
      setOpeningApp(false);
    }
  };
  const [compose, setCompose] = useState<ComposeContext | null>(null);

  const marketing = data?.marketing ?? null;
  const contract = data?.contract ?? null;
  const margin30d =
    marketing?.bookingsValueUsd30d != null &&
    contract?.commissionRate != null &&
    contract.commissionRate > 0 &&
    marketing?.spend30d != null
      ? marketing.bookingsValueUsd30d * contract.commissionRate -
        marketing.spend30d
      : null;

  return (
    <Page>
      <div className="flex-1 lg:h-[100dvh] overflow-y-auto min-w-0 bg-white">
        <div className="w-full px-4 md:px-6 py-4 flex flex-col gap-3 min-h-0">
          {/* Header — breadcrumb + title + actions */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 flex-wrap min-w-0">
              <button
                onClick={() => navigate("/crm/customers")}
                className="inline-flex items-center gap-1 text-xs text-content-tertiary hover:text-content-primary transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-3.5 h-3.5"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
                Customers
              </button>
              {!isLoading && data && (
                <>
                  <span className="text-content-tertiary">/</span>
                  <h1 className="text-lg font-semibold text-content-primary truncate max-w-[360px] md:max-w-[520px]">
                    {data.customer.hotelName ?? data.customer.primaryEmail}
                  </h1>
                  <span
                    className={clsx(
                      "text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium",
                      data.customer.productType === "marketing"
                        ? "bg-blue-50 text-blue-700"
                        : "bg-violet-50 text-violet-700",
                    )}
                  >
                    {data.customer.productType === "marketing"
                      ? "Marketing"
                      : "All-in-one"}
                  </span>
                </>
              )}
            </div>
            {!isLoading && data && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetch()}
                  className="px-3 py-1.5 text-xs font-medium text-content-secondary hover:text-content-primary rounded-md hover:bg-gray-100 transition-colors"
                >
                  Refresh
                </button>
                <button
                  onClick={() =>
                    setCompose({
                      mode: "new",
                      customerDisplayName: data.customer.hotelName ?? undefined,
                      customerEmail: data.customer.primaryEmail ?? undefined,
                    })
                  }
                  className="px-3.5 py-1.5 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary/90 shadow-sm transition-colors"
                >
                  New email
                </button>
              </div>
            )}
          </div>

          {isError && (
            <div className="flex items-center gap-3 px-4 py-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700">
              <span>Failed to load customer data.</span>
              <button
                onClick={() => refetch()}
                className="font-medium underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {isLoading && <PageSkeleton />}

          {!isLoading && data && (
            <>
              {/* Hero strip — 7 tiles, grid so they distribute evenly and
                  fill the full width with no trailing dead space. */}
              <div className="rounded-xl border border-border-secondary shadow-sm overflow-hidden">
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-y sm:divide-y-0 lg:divide-y-0 divide-border-secondary">
                  <Tile
                    label="Health"
                    value={
                      data.healthScore?.band
                        ? data.healthScore.score != null
                          ? `${data.healthScore.score}`
                          : "n/a"
                        : "n/a"
                    }
                    accent={
                      data.healthScore?.band === "healthy"
                        ? "green"
                        : data.healthScore?.band === "watch"
                          ? "amber"
                          : data.healthScore?.band === "at_risk" ||
                              data.healthScore?.band === "concerning"
                            ? "red"
                            : "neutral"
                    }
                    sub={
                      data.healthScore?.band
                        ? data.healthScore.band.replace("_", " ")
                        : "insufficient data"
                    }
                  />
                  <Tile
                    label="Last active"
                    value={relativeShort(data.lastInteractionAt)}
                  />
                  <Tile
                    label="30-day margin"
                    value={formatUsd(margin30d)}
                    accent={
                      margin30d != null
                        ? margin30d > 0
                          ? "green"
                          : margin30d < 0
                            ? "red"
                            : "neutral"
                        : "neutral"
                    }
                  />
                  <Tile
                    label="Ad spend 30d"
                    value={formatUsd(marketing?.spend30d ?? null)}
                  />
                  <Tile
                    label="Bookings 30d"
                    value={marketing?.bookings30d ?? "—"}
                  />
                  <Tile
                    label="Open tickets"
                    value={data.openTicketCount}
                    accent={data.openTicketCount > 0 ? "amber" : "neutral"}
                  />
                  <Tile
                    label="Commission"
                    value={
                      contract?.commissionRate != null
                        ? `${Math.round(contract.commissionRate * 100)}%`
                        : "—"
                    }
                  />
                </div>
              </div>

              {/* Tabs */}
              <TabBar
                active={activeTab}
                onChange={setActiveTab}
                openTickets={data.openTicketCount}
                onOpenApp={handleOpenApp}
                openingApp={openingApp}
              />

              {/* ─── Overview tab ─── */}
              {activeTab === "overview" && (
                <div className="flex flex-col gap-3">
                  {/* Row 1 — 50/50 split. `items-start` stops grid from
                      stretching cells, so each card sizes to its own content
                      and there's no dead space at the bottom of the shorter
                      cards. The right column is natural height (Health +
                      Campaigns), AI Summary is its own natural height. */}
                  <div className="grid grid-cols-12 gap-3 items-start">
                    <div className="col-span-12 lg:col-span-6">
                      <AiSummarySection customerId={data.customer.id} />
                    </div>
                    <div className="col-span-12 lg:col-span-6 flex flex-col gap-3">
                      <HealthSection
                        customerId={data.customer.id}
                        healthScore={data.healthScore}
                      />
                      <section className="bg-white rounded-xl border border-border-secondary shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
                          <h2 className="text-sm font-semibold text-content-primary">
                            Campaigns
                          </h2>
                          <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
                            Google Ads · last 30 days
                          </span>
                        </div>
                        <div className="max-h-[420px] overflow-auto">
                          <CampaignSummaryTable
                            breakdown={
                              data.marketing?.campaignCategoryBreakdown ?? null
                            }
                          />
                        </div>
                      </section>
                    </div>
                  </div>

                  {/* Marketing — 4 hero tiles + 7 mini tiles, full width */}
                  <MarketingSection
                    marketing={data.marketing}
                    onRefresh={() => refreshMarketing.mutate()}
                    refreshing={refreshMarketing.isPending}
                  />

                  {/* Recent feedback — compact read-only list */}
                  <FeedbackPreview feedback={data.feedbackHistory} />

                  {/* Admin actions — collapsible so they can stay out of the
                      way when the CSM is just skimming. Closed by default. */}
                  <details className="group bg-white rounded-xl border border-border-secondary shadow-sm overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-3.5 h-3.5 text-content-tertiary transition-transform group-open:rotate-90"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                        <h3 className="text-sm font-semibold text-content-primary">
                          Admin actions
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
                          contacts · owners · linked accounts · email & website apps · next action · churn flag · notes
                        </span>
                      </div>
                    </summary>
                    <div className="border-t border-border-secondary p-3 bg-gray-50/40 flex flex-col gap-3">
                      <div className="grid grid-cols-12 gap-3 items-start">
                        <div className="col-span-12 md:col-span-6 lg:col-span-3">
                          <OverviewSection snapshot={data} />
                        </div>
                        <div className="col-span-12 md:col-span-6 lg:col-span-3 flex flex-col gap-3">
                          <OwnersSection customerId={data.customer.id} />
                          <LinkedAccountsSection
                            userId={data.customer.id}
                            userDisplayName={
                              data.customer.hotelName ?? undefined
                            }
                          />
                        </div>
                        {/* Product apps grouped together */}
                        <div className="col-span-12 md:col-span-6 lg:col-span-3 flex flex-col gap-3">
                          <EmailMarketingSection
                            customerId={data.customer.id}
                            enabled={data.customer.emailMarketingEnabled}
                            profile={data.customer.emailMarketing}
                          />
                          <WebsiteBuilderSection
                            customerId={data.customer.id}
                            enabled={data.customer.websiteBuilderEnabled}
                          />
                        </div>
                        <div className="col-span-12 md:col-span-6 lg:col-span-3 flex flex-col gap-3">
                          <NextActionSection customerId={data.customer.id} />
                          <ChurnRiskFlagSection customerId={data.customer.id} />
                        </div>
                      </div>
                      <NotesSection customerId={data.customer.id} />
                    </div>
                  </details>
                </div>
              )}

              {/* ─── Contract & Billing tab ─── */}
              {activeTab === "billing" && (
                <div className="max-w-4xl">
                  <ContractBillingSection
                    customerId={data.customer.id}
                    contract={data.contract}
                  />
                </div>
              )}

              {/* ─── Communications tab ─── */}
              {activeTab === "communications" && (
                <div className="max-w-5xl space-y-4">
                  <EmailTimelineSection customerId={data.customer.id} />
                  <TimelineSection
                    customerId={data.customer.id}
                    mode="communications"
                    onReply={(ctx) =>
                      setCompose({
                        ...ctx,
                        customerDisplayName:
                          data.customer.hotelName ?? undefined,
                        customerEmail: data.customer.primaryEmail ?? undefined,
                      })
                    }
                  />
                </div>
              )}

              {/* ─── Meetings tab ─── */}
              {activeTab === "meetings" && (
                <div className="max-w-5xl">
                  <MeetingsSection customerId={data.customer.id} />
                </div>
              )}

              {/* ─── App Activity tab ─── */}
              {activeTab === "app-activity" && (
                <div className="max-w-5xl">
                  <AppUsageSummaryCard customerId={data.customer.id} />
                  <TimelineSection
                    customerId={data.customer.id}
                    mode="app-activity"
                  />
                </div>
              )}

              {/* ─── Ads Optimization tab ─── */}
              {activeTab === "ads-optimization" && (
                <div className="max-w-6xl space-y-4">
                  <AdsOptimizationSection customerId={data.customer.id} />
                  <AdsOptimizationDecisionLog customerId={data.customer.id} />
                  <AdsOptimizationConfigHistory
                    customerId={data.customer.id}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {compose && id && (
        <ComposeEmailModal
          customerId={id}
          context={compose}
          onClose={() => setCompose(null)}
        />
      )}
    </Page>
  );
}
